import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { apiRequest } from '../lib/utils';
import { 
  FileText, 
  MessageSquare, 
  Star, 
  Save,
  Plus,
  Trash2,
  Edit,
  ChevronLeft,
  Image,
  Move
} from 'lucide-react';

const AdminContentPage = () => {
  const [content, setContent] = useState({});
  const [testimonials, setTestimonials] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [newTestimonial, setNewTestimonial] = useState({ author_name: '', author_title: '', content: '' });
  const [newImage, setNewImage] = useState({ location: '', url: '', position: 'center' });

  // Content sections that can be edited
  const contentSections = [
    { key: 'home_hero_title', label: 'Home Page - Hero Title', type: 'text', default: 'Then, Now & Always' },
    { key: 'home_hero_subtitle', label: 'Home Page - Hero Subtitle', type: 'textarea', default: 'Archon Insurance Agency provides comprehensive property and casualty insurance solutions for Massachusetts businesses and families.' },
    { key: 'about_mission', label: 'About Page - Mission Statement', type: 'textarea', default: 'Our mission is to provide exceptional insurance solutions while building lasting relationships with our clients.' },
    { key: 'about_history', label: 'About Page - Company History', type: 'textarea', default: 'Founded with a commitment to serve the Massachusetts community...' },
    { key: 'services_intro', label: 'Services Page - Introduction', type: 'textarea', default: 'We offer comprehensive insurance solutions tailored to your needs.' },
    { key: 'contact_address', label: 'Contact - Address', type: 'text', default: 'Massachusetts' },
    { key: 'footer_tagline', label: 'Footer - Tagline', type: 'text', default: 'Licensed in Massachusetts' },
  ];

  // Image locations that can be customized
  const imageLocations = [
    { key: 'home_hero', label: 'Home Page - Hero Banner' },
    { key: 'about_banner', label: 'About Page - Banner' },
    { key: 'services_banner', label: 'Services Page - Banner' },
    { key: 'specializations_banner', label: 'Specializations Page - Banner' },
    { key: 'contact_banner', label: 'Contact Page - Banner' },
  ];

  const positionOptions = [
    { value: 'top', label: 'Top' },
    { value: 'center', label: 'Center' },
    { value: 'bottom', label: 'Bottom' },
    { value: 'left', label: 'Left' },
    { value: 'right', label: 'Right' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contentData, testimonialData, imageData] = await Promise.all([
        apiRequest('/admin/content'),
        apiRequest('/admin/testimonials'),
        apiRequest('/admin/images').catch(() => [])
      ]);
      
      // Convert array to object keyed by 'key'
      const contentObj = {};
      (contentData || []).forEach(item => {
        contentObj[item.key] = item.value;
      });
      setContent(contentObj);
      setTestimonials(testimonialData || []);
      setImages(imageData || []);
    } catch (error) {
      console.error('Failed to fetch content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
  };

  const saveContent = async (key) => {
    setSaving(true);
    try {
      await apiRequest(`/admin/content/${key}?value=${encodeURIComponent(content[key] || '')}`, {
        method: 'PUT'
      });
      toast.success('Content saved!');
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const saveAllContent = async () => {
    setSaving(true);
    try {
      for (const section of contentSections) {
        if (content[section.key]) {
          await apiRequest(`/admin/content/${section.key}?value=${encodeURIComponent(content[section.key])}`, {
            method: 'PUT'
          });
        }
      }
      toast.success('All content saved!');
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const addTestimonial = async () => {
    if (!newTestimonial.author_name || !newTestimonial.content) {
      toast.error('Please fill in author name and content');
      return;
    }
    
    try {
      const params = new URLSearchParams({
        author_name: newTestimonial.author_name,
        author_title: newTestimonial.author_title,
        content: newTestimonial.content,
        display_order: testimonials.length
      });
      
      const result = await apiRequest(`/admin/testimonials?${params}`, { method: 'POST' });
      setTestimonials([...testimonials, result]);
      setNewTestimonial({ author_name: '', author_title: '', content: '' });
      toast.success('Testimonial added!');
    } catch (error) {
      toast.error('Failed to add testimonial');
    }
  };

  const updateTestimonial = async (id, updates) => {
    try {
      const params = new URLSearchParams(updates);
      await apiRequest(`/admin/testimonials/${id}?${params}`, { method: 'PUT' });
      setTestimonials(testimonials.map(t => t.id === id ? { ...t, ...updates } : t));
      setEditingTestimonial(null);
      toast.success('Testimonial updated!');
    } catch (error) {
      toast.error('Failed to update testimonial');
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    
    try {
      await apiRequest(`/admin/testimonials/${id}`, { method: 'DELETE' });
      setTestimonials(testimonials.filter(t => t.id !== id));
      toast.success('Testimonial deleted!');
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  // Image management functions
  const saveImage = async (location, url, position) => {
    setSaving(true);
    try {
      const params = new URLSearchParams({ location, url, position });
      await apiRequest(`/admin/images?${params}`, { method: 'POST' });
      
      // Update local state
      const existingIndex = images.findIndex(img => img.location === location);
      if (existingIndex >= 0) {
        const updated = [...images];
        updated[existingIndex] = { location, url, position };
        setImages(updated);
      } else {
        setImages([...images, { location, url, position }]);
      }
      toast.success('Image saved!');
    } catch (error) {
      toast.error('Failed to save image');
    } finally {
      setSaving(false);
    }
  };

  const getImageForLocation = (location) => {
    return images.find(img => img.location === location) || { url: '', position: 'center' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-content-page">
        <div className="mb-6">
          <Link to="/admin" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Dashboard
          </Link>
          <h1 className="text-2xl font-bold text-slate-900">Content Management</h1>
          <p className="text-slate-600 mt-1">Edit website content without coding</p>
        </div>

        <Tabs defaultValue="content" className="space-y-6">
          <TabsList>
            <TabsTrigger value="content" className="flex items-center gap-2">
              <FileText className="h-4 w-4" /> Page Content
            </TabsTrigger>
            <TabsTrigger value="images" className="flex items-center gap-2">
              <Image className="h-4 w-4" /> Images
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="flex items-center gap-2">
              <Star className="h-4 w-4" /> Testimonials
            </TabsTrigger>
          </TabsList>

          <TabsContent value="content">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Edit Page Content</CardTitle>
                <Button onClick={saveAllContent} disabled={saving} className="bg-[#1a365d]">
                  <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save All Changes'}
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                {contentSections.map((section) => (
                  <div key={section.key} className="border-b pb-4 last:border-0">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {section.label}
                    </label>
                    {section.type === 'textarea' ? (
                      <textarea
                        className="w-full border rounded-md p-3 min-h-[100px] text-sm"
                        value={content[section.key] || section.default}
                        onChange={(e) => handleContentChange(section.key, e.target.value)}
                        placeholder={section.default}
                      />
                    ) : (
                      <input
                        type="text"
                        className="w-full border rounded-md p-3 text-sm"
                        value={content[section.key] || section.default}
                        onChange={(e) => handleContentChange(section.key, e.target.value)}
                        placeholder={section.default}
                      />
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={() => saveContent(section.key)}
                      disabled={saving}
                    >
                      Save This Section
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="images">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="h-5 w-5" /> Manage Page Images
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-6">
                  Update banner images and their positioning for each page. Enter an image URL and select how you want the image positioned.
                </p>
                
                <div className="space-y-6">
                  {imageLocations.map((loc) => {
                    const currentImage = getImageForLocation(loc.key);
                    return (
                      <div key={loc.key} className="border rounded-lg p-4">
                        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                          <Move className="h-4 w-4 text-slate-400" />
                          {loc.label}
                        </h3>
                        
                        <div className="grid gap-4 md:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Image URL</label>
                            <input
                              type="text"
                              className="w-full border rounded-md p-2 text-sm"
                              placeholder="https://images.unsplash.com/..."
                              defaultValue={currentImage.url}
                              id={`img-url-${loc.key}`}
                            />
                            <p className="text-xs text-slate-500 mt-1">Paste a URL from Unsplash, your hosting, or any image URL</p>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Position</label>
                            <select
                              className="w-full border rounded-md p-2 text-sm"
                              defaultValue={currentImage.position || 'center'}
                              id={`img-pos-${loc.key}`}
                            >
                              {positionOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                            <p className="text-xs text-slate-500 mt-1">How the image is aligned in the banner</p>
                          </div>
                        </div>
                        
                        {currentImage.url && (
                          <div className="mt-3">
                            <p className="text-xs text-slate-500 mb-1">Current Preview:</p>
                            <div 
                              className="h-24 rounded bg-cover bg-no-repeat border"
                              style={{ 
                                backgroundImage: `url(${currentImage.url})`,
                                backgroundPosition: currentImage.position || 'center'
                              }}
                            />
                          </div>
                        )}
                        
                        <Button 
                          size="sm" 
                          className="mt-3 bg-[#1a365d]"
                          onClick={() => {
                            const url = document.getElementById(`img-url-${loc.key}`).value;
                            const position = document.getElementById(`img-pos-${loc.key}`).value;
                            saveImage(loc.key, url, position);
                          }}
                          disabled={saving}
                        >
                          <Save className="h-4 w-4 mr-2" /> Save Image
                        </Button>
                      </div>
                    );
                  })}
                </div>
                
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Tips for Images</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Use high-resolution images (at least 1920px wide)</li>
                    <li>• Free images: <a href="https://unsplash.com" target="_blank" rel="noopener noreferrer" className="underline">unsplash.com</a> or <a href="https://pexels.com" target="_blank" rel="noopener noreferrer" className="underline">pexels.com</a></li>
                    <li>• Position "center" works best for most images</li>
                    <li>• Changes may take a moment to appear on the live site</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="testimonials">
            <Card>
              <CardHeader>
                <CardTitle>Manage Testimonials</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Add New Testimonial */}
                <div className="bg-slate-50 p-4 rounded-lg mb-6">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Plus className="h-4 w-4" /> Add New Testimonial
                  </h3>
                  <div className="grid gap-3">
                    <input
                      type="text"
                      placeholder="Author Name"
                      className="border rounded-md p-2"
                      value={newTestimonial.author_name}
                      onChange={(e) => setNewTestimonial({ ...newTestimonial, author_name: e.target.value })}
                    />
                    <input
                      type="text"
                      placeholder="Author Title/Company"
                      className="border rounded-md p-2"
                      value={newTestimonial.author_title}
                      onChange={(e) => setNewTestimonial({ ...newTestimonial, author_title: e.target.value })}
                    />
                    <textarea
                      placeholder="Testimonial content..."
                      className="border rounded-md p-2 min-h-[80px]"
                      value={newTestimonial.content}
                      onChange={(e) => setNewTestimonial({ ...newTestimonial, content: e.target.value })}
                    />
                    <Button onClick={addTestimonial} className="bg-[#1a365d]">
                      <Plus className="h-4 w-4 mr-2" /> Add Testimonial
                    </Button>
                  </div>
                </div>

                {/* Existing Testimonials */}
                <div className="space-y-4">
                  {testimonials.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No testimonials yet. Add your first one above!</p>
                  ) : (
                    testimonials.map((testimonial) => (
                      <div key={testimonial.id} className="border rounded-lg p-4">
                        {editingTestimonial === testimonial.id ? (
                          <div className="space-y-3">
                            <input
                              type="text"
                              className="w-full border rounded-md p-2"
                              defaultValue={testimonial.author_name}
                              id={`edit-name-${testimonial.id}`}
                            />
                            <input
                              type="text"
                              className="w-full border rounded-md p-2"
                              defaultValue={testimonial.author_title}
                              id={`edit-title-${testimonial.id}`}
                            />
                            <textarea
                              className="w-full border rounded-md p-2 min-h-[80px]"
                              defaultValue={testimonial.content}
                              id={`edit-content-${testimonial.id}`}
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => {
                                  const name = document.getElementById(`edit-name-${testimonial.id}`).value;
                                  const title = document.getElementById(`edit-title-${testimonial.id}`).value;
                                  const content = document.getElementById(`edit-content-${testimonial.id}`).value;
                                  updateTestimonial(testimonial.id, { author_name: name, author_title: title, content });
                                }}
                              >
                                Save
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingTestimonial(null)}>
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium">{testimonial.author_name}</p>
                                <p className="text-sm text-slate-500">{testimonial.author_title}</p>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => updateTestimonial(testimonial.id, { is_active: !testimonial.is_active })}
                                >
                                  {testimonial.is_active ? 'Hide' : 'Show'}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setEditingTestimonial(testimonial.id)}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteTestimonial(testimonial.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <p className="mt-2 text-slate-600 italic">"{testimonial.content}"</p>
                            {!testimonial.is_active && (
                              <span className="inline-block mt-2 text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded">Hidden</span>
                            )}
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  );
};

export default AdminContentPage;
